import type { CaseFilters } from "../../services";
import { ALL_INCOMING_COLUMNS, ALL_QUEUE_COLUMNS } from "./queueColumnConfig";
import type {
  PathologistQueueTabView,
  PathologistQueueViewTab,
} from "./pathologistQueueViewPreference";
import type { QueueSortState } from "./pathologistReviewQueueView";
import { isDateOnlyString } from "../../utils/dateOnly";

export const PATHOLOGIST_QUEUE_DEFAULT_PREFERENCE_KEY =
  "pathologist_review_queue_default_v1";

export function getPathologistQueueDefaultPreferenceKey(
  tab: PathologistQueueViewTab
): string {
  return tab === "review"
    ? PATHOLOGIST_QUEUE_DEFAULT_PREFERENCE_KEY
    : `pathologist_${tab}_queue_default_v1`;
}

export interface PathologistQueueDefault extends Record<string, unknown> {
  version: 1;
  filters: CaseFilters;
  sorts: QueueSortState[];
  summaryFilter: string | null;
}

const ARRAY_FIELDS: (keyof CaseFilters)[] = [
  "currentStatusIds",
  "facilityIds",
  "workflowIds",
  "procedureIds",
  "flagDefinitionIds",
  "prefixKeys",
];
const DATE_FIELDS: (keyof CaseFilters)[] = [
  "dateFrom",
  "dateTo",
  "dateCollected",
];
const BOOLEAN_FIELDS: (keyof CaseFilters)[] = [
  "isBlocked",
  "isRush",
  "isHighPriority",
  "tatOverdue",
  "hasPendingTests",
];
const ALLOWED_FIELDS = new Set<keyof CaseFilters>([
  ...ARRAY_FIELDS,
  ...DATE_FIELDS,
  ...BOOLEAN_FIELDS,
  "physicianId",
  "caseType",
  "tatMinDays",
]);
const INCOMING_STEPS = new Set([
  "grossing",
  "embedding",
  "cutting",
  "staining",
  "awaiting_send_out",
  "pending_stains",
]);
const CASE_TYPES = new Set(["Malignancy", "Addendum", "Correction"]);
const TAT_MIN_DAYS = new Set([1, 2, 3, 5]);
const SORT_COLUMNS = new Set(
  ALL_QUEUE_COLUMNS.filter((column) => column.sortable).map(
    (column) => column.id
  )
);
const INCOMING_SORT_COLUMNS = new Set(
  ALL_INCOMING_COLUMNS.filter((column) => column.sortable).map(
    (column) => column.id
  )
);
const CORRECTIONS_SORT_COLUMNS = new Set([
  "caseNumber",
  "patient",
  "requestor",
  "fieldCount",
]);
const SUMMARY_FILTERS = new Set([
  "new",
  "stat",
  "overdue",
  "pendingStains",
  "discussion",
  "malignancy",
  "qaReview",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validPrefix(value: string): boolean {
  return value === "__OTHER__" || /^[A-Z]{1,16}$/.test(value);
}

function validFilterValue(key: string, value: unknown): boolean {
  if (
    ARRAY_FIELDS.includes(key as keyof CaseFilters) ||
    key === "pathologistIds"
  ) {
    return (
      Array.isArray(value) &&
      value.length <= 32 &&
      value.every((item) => typeof item === "string" && item.length > 0)
    );
  }
  if (DATE_FIELDS.includes(key as keyof CaseFilters)) {
    return isDateOnlyString(value);
  }
  if (BOOLEAN_FIELDS.includes(key as keyof CaseFilters))
    return typeof value === "boolean";
  if (key === "caseType")
    return typeof value === "string" && CASE_TYPES.has(value);
  if (key === "tatMinDays")
    return typeof value === "number" && TAT_MIN_DAYS.has(value);
  if (key === "currentStep")
    return typeof value === "string" && INCOMING_STEPS.has(value);
  return key === "physicianId" && typeof value === "string" && value.length > 0;
}

function isAllowedFilterField(
  key: string,
  tab: PathologistQueueViewTab
): boolean {
  if (tab === "corrections") return key === "prefixKeys";
  if (ALLOWED_FIELDS.has(key as keyof CaseFilters)) return true;
  if (key === "currentStep") return tab === "incoming";
  return key === "pathologistIds" && (tab === "all" || tab === "sentOut");
}

function decodeFilters(
  raw: unknown,
  prefixOptions: string[] | undefined,
  tab: PathologistQueueViewTab
): CaseFilters | null {
  if (!isRecord(raw)) return null;
  const filters: Record<string, unknown> = {};
  const valid = Object.entries(raw).every(([key, value]) => {
    if (!isAllowedFilterField(key, tab) || !validFilterValue(key, value)) {
      return false;
    }
    if (
      key === "prefixKeys" &&
      !(value as string[]).every(
        (prefix) => validPrefix(prefix) && prefixOptions?.includes(prefix)
      )
    )
      return false;
    filters[key] = value;
    return true;
  });
  return valid ? (filters as CaseFilters) : null;
}

function decodeSorts(
  raw: unknown,
  tab: PathologistQueueViewTab
): QueueSortState[] | null {
  const allowed =
    tab === "incoming"
      ? INCOMING_SORT_COLUMNS
      : tab === "corrections"
      ? CORRECTIONS_SORT_COLUMNS
      : SORT_COLUMNS;
  if (!Array.isArray(raw) || raw.length > allowed.size) return null;
  const sorts = raw.map((item) => {
    if (
      !isRecord(item) ||
      Object.keys(item).some(
        (key) => key !== "column" && key !== "direction"
      ) ||
      typeof item.column !== "string" ||
      !allowed.has(item.column) ||
      (item.direction !== "asc" && item.direction !== "desc")
    )
      return null;
    return { column: item.column, direction: item.direction };
  });
  if (sorts.some((sort) => !sort)) return null;
  const validSorts = sorts as QueueSortState[];
  return new Set(validSorts.map((sort) => sort.column)).size === sorts.length
    ? validSorts
    : null;
}

export function decodePathologistQueueDefault(
  raw: unknown,
  prefixOptions?: string[],
  tab: PathologistQueueViewTab = "review"
): PathologistQueueDefault | null {
  if (
    !isRecord(raw) ||
    raw.version !== 1 ||
    Object.keys(raw).some(
      (key) => !["version", "filters", "sorts", "summaryFilter"].includes(key)
    ) ||
    (raw.summaryFilter !== null &&
      (typeof raw.summaryFilter !== "string" ||
        tab !== "review" ||
        !SUMMARY_FILTERS.has(raw.summaryFilter)))
  )
    return null;
  const filters = decodeFilters(raw.filters, prefixOptions, tab);
  const sorts = decodeSorts(raw.sorts, tab);
  return filters && sorts
    ? {
        version: 1,
        filters,
        sorts,
        summaryFilter: raw.summaryFilter,
      }
    : null;
}

export function createPathologistQueueDefault(
  view: PathologistQueueTabView,
  prefixOptions?: string[],
  tab: PathologistQueueViewTab = "review"
): PathologistQueueDefault | null {
  const filters = Object.fromEntries(
    Object.entries(view.filters).filter(
      ([key, value]) => isAllowedFilterField(key, tab) && value !== undefined
    )
  );
  return decodePathologistQueueDefault(
    {
      version: 1,
      filters,
      sorts: view.sorts ?? (view.sort ? [view.sort] : []),
      summaryFilter: tab === "review" ? view.summaryFilter ?? null : null,
    },
    prefixOptions,
    tab
  );
}

export function applyPathologistQueueDefault(
  current: PathologistQueueTabView,
  saved: PathologistQueueDefault,
  tab: PathologistQueueViewTab = "review"
): PathologistQueueTabView {
  return {
    ...current,
    filters: saved.filters,
    sort: null,
    sorts: saved.sorts,
    page: 1,
    blockedPage: 1,
    ...(tab === "review" ? { summaryFilter: saved.summaryFilter } : {}),
  };
}
